export class Utils {
    /**
     * Evaluates a provided number to see if its even.
     * @param num Provided number to be evaluated.
     * @returns a boolean asserting if the number was even or not.
     */
    static IsEven(num) {
        return num % 2 == 0;
    }
    /**
     * Evaluates a provided number to see if it's odd.
     * @param num Provided number to be evaluated.
     * @returns a boolean asserting if the number was odd or not.
     */
    static IsOdd(num) {
        return !Utils.IsEven(num);
    }
    /**
     * Taking in a string, removes trailing whitespaces and extra spaces between words.
     * @param str String to be formatted.
     * @returns Formatted string without trailing whitespaces and extra spaces between words.
     */
    static trimAndReduce(str) {
        return str.trim().replace("\s+", " ");
    }
    /**
     * Taking in a number and a currency code, returns a string with the number formatted as the given currency.
     * @param number Number to be formatted as currency.
     * @param currency Currency code to format the number as, should be in ISO 4217 format (ex: USD, EUR, GBP, etc.)
     * @returns A string with the number formatted as the given currency.
     */
    static toCurrency(number, currency) {
        return Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(number);
    }
    /**
     * Taking in an element to be checked, checks if the content of the element is overflowing the size of the element or not.
     * @param element The Element to be checked for overflow.
     * @param horizontal A boolean to determine if the check should only be for the horizontal axis.
     * @param vertical A boolean to determine if the check should only be for the vertical axis.
     * @returns A boolean state determining if the element is overflowing.
     */
    static elementIsOverflowing(element, horizontal = false, vertical = false) {
        if (horizontal) {
            return element.scrollWidth > element.clientWidth;
        }
        else if (vertical) {
            return element.scrollHeight > element.clientHeight;
        }
        else {
            return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
        }
    }
    /**
     * Taking the root styles of the given page and the provided rem value returns the rem value as pixels.
     * @param rem String or Number rem value to be parsed and converted into pixels.
     * @returns A pixel interpretation of the provided rem value.
     */
    static convertRemToPixels(rem) {
        const pxToRem = parseFloat(getComputedStyle(document.documentElement).fontSize);
        let numberRem = 0;
        if (typeof rem !== "number") {
            rem = Utils.trimAndReduce(rem);
            if (rem.includes("rem")) {
                rem = rem.replace("rem", "");
            }
            numberRem = parseFloat(rem);
        }
        else {
            numberRem = rem;
        }
        return numberRem * pxToRem;
    }
    /**
     * Taking in an element evaluates if the element is visible within the screens viewport.
     * @param element Element to be evaluated for visibility within the screens viewport.
     * @param horizontalOnly Optional Parameter that checks if the element is visible only caring about the horizontal axis.
     * @param verticalOnly Optional Parameter that checks if the element is visible only caring about the vertical axis.
     * @returns Boolean representation if the element is visible within the viewport.
     */
    static IsVisibleInViewport(element, horizontalOnly = false, verticalOnly = false) {
        const rect = element.getBoundingClientRect();
        if (horizontalOnly) {
            return (rect.left >= 0 &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth));
        }
        if (verticalOnly) {
            return (rect.top >= 0 &&
                rect.bottom <= ((window.innerHeight + rect.height) || document.documentElement.clientHeight));
        }
        return (rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= ((window.innerHeight + rect.height) || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth));
    }
    ;
    /**
    * Taking in an element evaluates if the element is visible within the given container.
    * @param element Element to be evaluated for visibility within the container.
    * @param container Container of element to evaluate against the element to check the elements visibility.
    * @param horizontalOnly Optional Parameter that checks if the element is visible only caring about the horizontal axis.
    * @param verticalOnly Optional Parameter that checks if the element is visible only caring about the vertical axis.
    * @param padding Optional Parameter that gives some pixels as margin for error on the card being in the contianer.
    * @returns Boolean representation if the element is visible within the container.
    */
    static IsVisibleInContainer(element, container, padding = 0, horizontalOnly = false, verticalOnly = false) {
        const rect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        if (horizontalOnly) {
            return (rect.left + padding >= containerRect.left &&
                rect.right - padding <= containerRect.right);
        }
        if (verticalOnly) {
            return (rect.top + padding >= containerRect.top &&
                rect.bottom - padding <= containerRect.bottom);
        }
        return (rect.top + padding >= containerRect.top &&
            rect.left + padding >= containerRect.left &&
            rect.bottom - padding <= containerRect.bottom &&
            rect.right - padding <= containerRect.right);
    }
    ;
    /**
     * A function to take the height of a reference element and assign the height to the target elements styles somehow.
     *
     * @param reference Used for getting the height of the desired element and using the height on the new element.
     * @param target The new element to recieve the height value from the reference element.
     * @param property The given style property the height should be assigned to
     */
    static copyHeight(reference, target, property = "height") {
        var referenceHeight = reference.clientHeight;
        target.style.setProperty((property), (referenceHeight + "px"));
    }
    ;
    /**
    * After scrolling a fixed distance display/ update the given element to make a seamless scrolling experience.
    * If no values are provided distance will be set to 0.
    *
    * @param targetElement The element to have the styles/ classes applied to.
    * @param rootElement The root node of the document to retrieve variables from.
    * @param heightStyleProperty Defaulting to "--nav-height" this variable is for selecting the correct style
    * on the root element for deciding the scroll distance.
    * @param classList Array of strings representing classes to be added and removed on scroll, strings starting with "!" are
    * removed when scroll distanceScrolled > distance given and added when distanceScrolled < distance
    * @param distanceOverride Optional override value to use when the root element distance isn't what you want.
    * @param distanceScrolled Optional parameter taking in the distance scrolled to avoid computing it twice if already calculated.
    * @returns A boolean value representing on when the Element should have all the classes apllied.
    */
    static updateClassesOnScroll(targetElement, rootElement, heightStyleProperty = "--nav-height", classList = [], distanceOverride, distanceScrolled) {
        var distance = 0;
        var updatedElement = false;
        if (classList.length > 0) {
            if (rootElement != null) {
                var variableHeight = rootElement.style.getPropertyValue(heightStyleProperty);
                if (variableHeight != '') {
                    distance = parseInt(variableHeight, 10);
                }
            }
            if (distance == 0 && distanceOverride != null) {
                distance = distanceOverride;
            }
            if (distanceScrolled == null) {
                distanceScrolled = (document.documentElement || document.body.parentNode || document.body).scrollTop;
            }
            if (distance <= distanceScrolled) {
                updatedElement = true;
                for (var i in classList) {
                    var className = classList[i];
                    if (className.startsWith("!")) {
                        targetElement.classList.remove(className.slice(1));
                    }
                    else {
                        targetElement.classList.add(className);
                    }
                }
            }
            else {
                updatedElement = false;
                for (var i in classList) {
                    var className = classList[i];
                    if (className.startsWith("!")) {
                        targetElement.classList.add(className.slice(1));
                    }
                    else {
                        targetElement.classList.remove(className);
                    }
                }
            }
        }
        return updatedElement;
    }
    ;
    /**
     * Create an IntersectionOberserverCallback to run on intersection success updating affected element with "scroll-visible" class.
     * @returns IntersectionObserverCallback to passed into and run from an IntersectionObserver.
     */
    static FadeIntoViewportCallback() {
        const callback = (entries) => {
            // Sort entries in order of when they triggered
            entries.sort(x => x.time);
            entries.forEach(entry => {
                // If the entry is intersecting when the callback was triggered
                if (entry.isIntersecting) {
                    const element = entry.target;
                    // Update the classList of the affected Element
                    element.classList.add("scroll-visible");
                }
            });
        };
        return callback;
    }
    /**
     * Creates and attaches IntersectionObservers to a given target element running required callback functionality on Intersection with optional container or viewport.
     * @param target Target element for the Intersection Observer to be attached to.
     * @param container Optional scrollable container for the Intersection Observer to run within, if not provided uses the screens viewport.
     * @param rootMargin How much area of the given container/ viewport should be ignored when seeing if an element is within view.
     * @param threshold How much of the affected element needs to be visible within the container/ viewport to trigger the Observer Intersected event.
     * @param callback An IntersectionObserverCallback to be run on Intersection with the target and container.
     */
    static RespondToVisibility(target, container, rootMargin = "0px 0px 0px 0px", threshold = 1.0, callback) {
        // Define the options for the Intersection Observer to use
        let options = {
            rootMargin: rootMargin,
            threshold: threshold
        };
        if (container != null) {
            options.root = container;
        }
        // Create the observer instace to be attached to the target
        const observer = new IntersectionObserver(callback, options);
        // Attach observer to target Element
        observer.observe(target);
    }
    /**
     * Gets the tracking data from the hidden fields on the page and packages them into a TrackingData object.
     * @returns A completed TrackingData object.
     */
    static GetTrackingDataFromHiddenFields() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        const fieldClass = '.hidden-tracking-field';
        return {
            landingPage: (_b = (_a = document.querySelector(`${fieldClass}[name=landingPage]`)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '',
            conversionPagePath: (_d = (_c = document.querySelector(`${fieldClass}[name=conversionPagePath]`)) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : '',
            gtmCode: (_f = (_e = document.querySelector(`${fieldClass}[name=gtmCode]`)) === null || _e === void 0 ? void 0 : _e.value) !== null && _f !== void 0 ? _f : '',
            spotCode: (_h = (_g = document.querySelector(`${fieldClass}[name=spotCode]`)) === null || _g === void 0 ? void 0 : _g.value) !== null && _h !== void 0 ? _h : '',
            campaignName: (_k = (_j = document.querySelector(`${fieldClass}[name=campaignName]`)) === null || _j === void 0 ? void 0 : _j.value) !== null && _k !== void 0 ? _k : '',
            campaignTerm: (_m = (_l = document.querySelector(`${fieldClass}[name=campaignTerm]`)) === null || _l === void 0 ? void 0 : _l.value) !== null && _m !== void 0 ? _m : '',
            campaignMedium: (_p = (_o = document.querySelector(`${fieldClass}[name=campaignMedium]`)) === null || _o === void 0 ? void 0 : _o.value) !== null && _p !== void 0 ? _p : '',
        };
    }
    /**
     * Attaches the provided callback to a set of user interaction events on the document.
     * @param callback The EventListener to be invoked when any of the registered events are triggered.
     * @returns A teardown function that, when called, removes the callback from all registered events.
     */
    static SetupLoadEventListener(callback) {
        document.addEventListener("mousemove", callback, false);
        document.addEventListener("mousedown", callback, false);
        document.addEventListener("keypress", callback, false);
        document.addEventListener("DOMMouseScroll", callback, false);
        document.addEventListener("scroll", callback, false);
        document.addEventListener("touchmove", callback, false);
        document.addEventListener("MSPointerMove", callback, false);
        return () => Utils.RemoveLoadEventListener(callback);
    }
    /**
     * Detaches the provided callback from the set of user interaction events on the document.
     * @param callback The EventListener to be removed from the registered events.
     */
    static RemoveLoadEventListener(callback) {
        document.removeEventListener("mousemove", callback, false);
        document.removeEventListener("mousedown", callback, false);
        document.removeEventListener("keypress", callback, false);
        document.removeEventListener("DOMMouseScroll", callback, false);
        document.removeEventListener("scroll", callback, false);
        document.removeEventListener("touchmove", callback, false);
        document.removeEventListener("MSPointerMove", callback, false);
    }
    /**
     * Taking in a Date String converts it into a date object and converts it into seconds to be evaluated for how long ago the date string was.
     * @param dateString The Date String to be evaluated
     * @returns A human readable string for how long ago was the date.
     */
    static timeAgo(dateString) {
        const date = Utils.convertDateStringToInt(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
        const intervals = {
            year: 31536000,
            month: 2592000,
            week: 604800,
            day: 86400,
            hour: 3600,
            minute: 60
        };
        const rtf = new Intl.RelativeTimeFormat('en', {
            numeric: 'auto'
        });
        for (const [unit, value] of Object.entries(intervals)) {
            const interval = Math.floor(seconds / value);
            if (interval >= 1) {
                return rtf.format(-interval, unit);
            }
        }
        return 'just now';
    }
    /**
     * Converts a Datestring in dd/mm/yyyy format into a Date object.
     * @param dateString The string to convert into a Date.
     * @returns A constructed Date object from provided string.
     */
    static convertDateStringToInt(dateString) {
        const [day, month, year] = dateString.split('/');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
}
